import _ from 'underscore';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { LegacyI18nContext } from '@bingads-webui-react/legacy-i18n-context';
import { usePilotsValue } from '@bingads-webui-campaign-react/basic-context-utils';
import {
  Field,
  Input,
  Label,
  Radio,
  RadioGroup,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { HelpButton } from '@bingads-webui-react/help-popup';
import { withValidation, withError, ValidationGroup } from '@bingads-webui-react/validation';
import { SearchableDropdown } from '@bingads-webui-campaign-react/goals-and-website-tile';
import { BusinessInfoCollector } from '@bingads-webui-campaign-react/pmax-language-country';
import { useResponsiveSize } from '@adscore/with-responsive-size';
import { Constants } from '@bingads-webui-theme-2018/theme-fluent';

import { businessNameSchema } from '../validation-schema';
import { businessNameValidationErrors } from '../consts';

const useBusinessInfoTableStyles = makeStyles({
  businessInfoTableContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: '527px',
  },
  countryBizNameContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    gap: '8px',
    alignItems: 'baseline',
  },
  'countryBizNameContainer-m': {
    flexDirection: 'column',
    alignItems: 'center',
  },
  'countryBizNameContainer-s': {
    flexDirection: 'column',
    alignItems: 'center',
  },
  countrySelectionContainer: {
    flex: 1,
    minWidth: 0,
  },
  'countrySelectionContainer-m': {
    width: '100%',
  },
  'countrySelectionContainer-s': {
    width: '100%',
  },
  businessNameContainer: {
    flex: 1,
    minWidth: 0,
  },
  biznameInputField: {},
  'businessNameContainer-m': {
    width: '100%',
    maxWidth: '100%',
  },
  'businessNameContainer-s': {
    width: '100%',
    maxWidth: '100%',
  },
  sectionDescription: {
    margin: '10px 0',
  },
  chinaTargetingContainer: {
    width: '100%',
    maxWidth: '600px',
    padding: '0 0 20px 0',
  },
  chinaTargetingLabel: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '4px',
    '& label': {
      display: 'inline-block',
      padding: '0px',
    },
  },
  chinaTargetingRadioGroup: {
    display: 'flex',
    flexDirection: 'row',
    gap: '8px',
    '& label': {
      fontWeight: tokens.fontWeightRegular,
    },
  },
});

const ChinaTargetingOptions = {
  OUTSIDE: 'outside',
  WITHIN: 'within',
};

const ValidatedInput = withError(withValidation(props => (<Input {...props} />)));

export function BusinessInfoTable(props) {
  const {
    bizInfoData,
    updateBizInfoData,
    setDataValid,
    alwaysShowError,
    activity,
    isTargetingInsideChina,
    setIsTargetingInsideChina,
  } = props;
  const i18n = useContext(I18nContext);
  const legacyI18n = useContext(LegacyI18nContext);
  const isPMaxChinaFlowEnabled = usePilotsValue('isPMaxChinaFlowEnabled');

  const classes = useBusinessInfoTableStyles();
  const displayMode = useResponsiveSize(Constants.ResponsiveSizeBreakpoints);
  const [businessNameInputError, setBusinessNameInputError] = useState('');
  const [showBusinessNameError, setShowBusinessNameError] = useState(false);
  const showBizNameErrorTimerRef = useRef(null);

  useEffect(() => () => {
    if (showBizNameErrorTimerRef.current) {
      clearTimeout(showBizNameErrorTimerRef.current);
    }
  }, []);

  const {
    businessName,
    countryCode,
    callingCountryCode,
    phoneNumber,
    email,
    supportedCountryList,
  } = bizInfoData || {};

  const countryOptions = useMemo(() => {
    const options = _.map(supportedCountryList, country => ({
      key: country.Code,
      text: country.Name,
    }));
    return _.sortBy(options, 'text');
  }, [supportedCountryList]);

  const onBizNameValidate = useCallback((e) => {
    setDataValid(prevState => ({
      ...prevState,
      isBizNameValid: e.isValid,
    }));
  }, [setDataValid]);

  const onBizInfoValidate = useCallback((isValid) => {
    setDataValid(prevState => ({
      ...prevState,
      isBizInfoValid: isValid,
    }));
  }, [setDataValid]);

  const businessNameErrorProcessor = useCallback((errors) => {
    const errorStrings = _.map(
      errors,
      (oldError, key) => (
        _.has(businessNameValidationErrors, key) ?
          i18n.getString(businessNameValidationErrors[key]) : oldError)
    );

    if (_.isEmpty(errorStrings)) {
      setBusinessNameInputError('');
      return [];
    }

    const customError = _.last(errorStrings);
    setBusinessNameInputError(customError);
    return [customError];
  }, [i18n]);

  const onCountrySelected = useCallback((e, selection) => {
    if (selection.key !== 'CN') {
      setIsTargetingInsideChina(false);
    }
    updateBizInfoData({
      countryCode: selection.key,
    });
  }, [setIsTargetingInsideChina, updateBizInfoData]);

  const onBizNameChange = useCallback(({ target: { value } }) => {
    updateBizInfoData({
      businessName: value,
    });
    setShowBusinessNameError(false);
    if (showBizNameErrorTimerRef.current) {
      clearTimeout(showBizNameErrorTimerRef.current);
    }
    showBizNameErrorTimerRef.current = setTimeout(() => {
      setShowBusinessNameError(true);
    }, 1500);
  }, [updateBizInfoData]);

  const onEmailChange = useCallback((newEmail) => {
    updateBizInfoData({
      email: newEmail,
    });
  }, [updateBizInfoData]);

  const onPhoneChange = useCallback(({
    changePhoneNumber, phoneNumber: newPhoneNumber, callingCode, callingCountryCode: newCallingCountryCode,
  }) => {
    if (changePhoneNumber) {
      updateBizInfoData({
        phoneNumber: newPhoneNumber,
      });
    } else {
      updateBizInfoData({
        callingCode,
        callingCountryCode: newCallingCountryCode,
      });
    }
  }, [updateBizInfoData]);

  const showChinaTargetingOptions = countryCode === 'CN' && isPMaxChinaFlowEnabled;

  const onChinaTargetingChange = useCallback((e, data) => {
    setIsTargetingInsideChina(data.value === ChinaTargetingOptions.WITHIN);
  }, [setIsTargetingInsideChina]);

  useEffect(() => {
    if (showChinaTargetingOptions) {
      activity.trace('[ChinaTargeting] Show China targeting radio group');
    }
  }, [activity, showChinaTargetingOptions]);

  return (
    <div className={mergeClasses('business-info-table-container', classes.businessInfoTableContainer)}>
      <div className={mergeClasses('country-bizname-container', classes.countryBizNameContainer, classes[`countryBizNameContainer-${displayMode}`])}>
        <div className={mergeClasses('country-selection-container', classes.countrySelectionContainer, classes[`countrySelectionContainer-${displayMode}`])}>
          <Label required>{i18n.getString(_TL_('Location'))}</Label>
          <SearchableDropdown
            aria-label={i18n.getString(_TL_('Select country of your business location'))}
            className="country-dropdown"
            selectedKey={countryCode}
            onChange={onCountrySelected}
            placeholder={i18n.getString(_TL_('Select country of your business location'))}
            options={countryOptions}
            dropdownWidth="auto"
          />
        </div>
        <div className={mergeClasses('business-name-container', classes.businessNameContainer, classes[`businessNameContainer-${displayMode}`])}>
          <ValidationGroup
            validationProps={{
              schema: businessNameSchema,
              i18n: legacyI18n,
              onValidate: onBizNameValidate,
            }}
          >
            <Field
              className={mergeClasses('bizname-input-field', classes.biznameInputField)}
              label={i18n.getString(_TL_('Business\'s Name'))}
              validationMessage={(alwaysShowError || showBusinessNameError) && businessNameInputError}
              required
            >
              <ValidatedInput
                id="business-name-input"
                value={businessName}
                onChange={onBizNameChange}
                validationProps={{
                  field: 'business',
                  errorProcessor: businessNameErrorProcessor,
                  alwaysShowError: alwaysShowError || showBusinessNameError,
                  debounce: 1500,
                  customErrorRendering: true,
                }}
                placeholder={i18n.getString(_TL_('Enter your business name'))}
                style={{ width: '100%' }}
              />
            </Field>
          </ValidationGroup>
        </div>
      </div>

      {showChinaTargetingOptions && (
        <Field
          className={mergeClasses('china-targeting-container', classes.chinaTargetingContainer)}
          label={{
            children: () => (
              <div className={mergeClasses('china-targeting-label', classes.chinaTargetingLabel)}>
                <Label required>{i18n.getString(_TL_('Will you target customers within or outside of Mainland China?'))}</Label>
                <HelpButton
                  aria-label={i18n.getString(_TL_('Learn more about targeting customers within or outside of Mainland China'))}
                  topic="pop_BA_Onboarding_China"
                />
              </div>
            ),
          }}
        >
          <RadioGroup
            value={isTargetingInsideChina ? ChinaTargetingOptions.WITHIN : ChinaTargetingOptions.OUTSIDE}
            onChange={onChinaTargetingChange}
            className={mergeClasses('china-targeting-radio-group', classes.chinaTargetingRadioGroup)}
          >
            <Radio id="within-china" value={ChinaTargetingOptions.WITHIN} label={i18n.getString(_TL_('Within Mainland China'))} />
            <Radio id="outside-china" value={ChinaTargetingOptions.OUTSIDE} label={i18n.getString(_TL_('Outside of Mainland China'))} />
          </RadioGroup>
        </Field>
      )}

      <div className="collect-business-info-container">
        <BusinessInfoCollector
          email={email}
          onEmailChange={onEmailChange}
          selectedCountryCode={callingCountryCode}
          countries={supportedCountryList}
          phoneNumber={phoneNumber}
          onPhoneChange={onPhoneChange}
          setIsValid={onBizInfoValidate}
          alwaysShowError={alwaysShowError}
        />
      </div >
    </div>
  );
}

BusinessInfoTable.propTypes = {
  bizInfoData: PropTypes.shape({
    country: PropTypes.string,
    businessName: PropTypes.string,
  }),
  updateBizInfoData: PropTypes.func,
  setDataValid: PropTypes.func,
  alwaysShowError: PropTypes.bool,
  isTargetingInsideChina: PropTypes.bool,
  setIsTargetingInsideChina: PropTypes.func,
  activity: PropTypes.shape({
    trace: PropTypes.func,
    error: PropTypes.func,
  }),
};

BusinessInfoTable.defaultProps = {
  bizInfoData: {},
  updateBizInfoData: _.noop,
  setDataValid: _.noop,
  alwaysShowError: false,
  isTargetingInsideChina: false,
  setIsTargetingInsideChina: _.noop,
  activity: {
    trace: _.noop,
    error: _.noop,
  },
};
